<template>
  <div 
    class="social-media-links relative justify-end"
    ref="socialContainer"
  >
    <div class="links-container">
      <a 
        v-for="(link, index) in socialLinks" 
        :key="link.name" 
        :href="link.url" 
        target="_blank" 
        rel="noopener noreferrer" 
        class="social-icon-wrapper"
        :style="{ '--delay': `${index * 0.1}s` }"
      >
        <div class="icon-background"></div>
        <div class="icon-glow"></div>
        <font-awesome-icon :icon="link.icon" class="social-icon" />
      </a>
    </div>
    
    <!-- Mobile toggle for social links -->
    <button 
      class="mobile-toggle" 
      @click="toggleMobile"
      v-if="isMobile"
    >
      <font-awesome-icon :icon="['fas', 'share-nodes']" />
      <span class="sr-only">Toggle Social Media</span>
    </button>
  </div>
</template>

<script>
export default {
  name: 'SocialMediaLinks',
  data() {
    return {
      socialLinks: [
        { name: 'Facebook', url: 'https://www.facebook.com/stainlesstankandequipment/', icon: ['fab', 'facebook'] },
        { name: 'Instagram', url: 'https://www.instagram.com/stainlesstanker/?hl=am-et', icon: ['fab', 'instagram'] },
        { name: 'LinkedIn', url: 'https://www.linkedin.com/company/stainlesstanker/posts/?feedView=all', icon: ['fab', 'linkedin'] },
        { name: 'Portal', url: 'https://www.ste-usa.com/web/96/#/login', icon: ['fab', 'product-hunt'] },
      ],
      isVisible: false,
      isMobileOpen: false,
      isMobile: false,
      observer: null
    };
  },
  mounted() {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
    
    // Set up intersection observer for entrance animation
    this.setupIntersectionObserver();
    
    // Trigger initial animation
    setTimeout(() => {
      this.isVisible = true;
    }, 300);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile);
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
    },
    toggleMobile() {
      this.isMobileOpen = !this.isMobileOpen;
      
      const container = this.$refs.socialContainer;
      if (container) {
        if (this.isMobileOpen) {
          container.classList.add('mobile-open');
        } else {
          container.classList.remove('mobile-open');
        }
      }
    },
    setupIntersectionObserver() {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      };
      
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      }, options);
      
      if (this.$refs.socialContainer) {
        this.observer.observe(this.$refs.socialContainer);
      }
    }
  }
};
</script>

<style scoped>
.social-media-links {
  display: flex;
  flex-direction: row;
  padding: 10px 10px 10px 0; /* Reduced left padding, kept right padding */
  z-index: 150;
  width: auto;
  justify-content: flex-end;
  margin-right: 0; /* Ensure alignment to right edge */
  margin-left: auto; /* Push to the right */
}

.links-container {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  
  @media (max-width: 768px) {
    position: fixed;
    top: 70px;
    right: -100%;
    background: rgba(29, 30, 38, 0.95);
    backdrop-filter: blur(10px);
    padding: 10px 15px;
    border-radius: 30px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.05);
    transition: right 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    flex-direction: column;
  }
}

.social-media-links.mobile-open .links-container {
  right: 20px;
}

.social-icon-wrapper {
  position: relative;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  opacity: 0;
  transform: translateY(10px);
  animation: social-icon-entrance 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  animation-delay: var(--delay);
  overflow: hidden;
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.social-media-links.in-view .social-icon-wrapper {
  opacity: 1;
  transform: translateY(0);
}

.icon-background {
  display: none; /* Remove background */
}

.icon-glow {
  position: absolute;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  opacity: 0;
  transform: scale(0);
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: 0;
}

.social-icon {
  color: #b7b9ba;
  font-size: clamp(1rem, 1vw, 1.25rem);
  position: relative;
  z-index: 2;
  transition: color 0.3s ease, transform 0.3s ease;
}

.social-icon-wrapper:hover {
  transform: translateY(-3px);
}

.social-icon-wrapper:hover .icon-glow {
  opacity: 0.5;
  transform: scale(1);
}

.social-icon-wrapper:hover .social-icon {
  color: #a22a21;
  transform: scale(1.25); /* Increased scale on hover */
}

/* Mobile toggle button */
.mobile-toggle {
  display: none;
  position: relative;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(143, 0, 0, 0.7), rgba(173, 0, 0, 0.9));
  color: white;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.mobile-toggle:hover, 
.social-media-links.mobile-open .mobile-toggle {
  transform: scale(1.1);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

/* Animation keyframes */
@keyframes social-icon-entrance {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .social-media-links {
    padding: 8px 5px;
  }
  
  .links-container {
    gap: 8px;
  }
  
  .social-icon-wrapper {
    width: 32px;
    height: 32px;
  }
}

@media (max-width: 992px) {
  .social-media-links {
    padding: 5px;
  }
  
  .links-container {
    gap: 6px;
  }
}
</style>
